import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { Book, Coins, Shield, TrendingUp, Users, Zap, ChevronRight, Play, Award, Globe, Lock, Network, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { DonationSection } from '@/components/DonationSection';

const Topics = () => {
  useSeoMeta({
    title: 'Temas Fundamentales de Bitcoin - Bitcoin Life Panamá',
    description: 'Programa exhaustivo diseñado para llevarte desde cero hasta experto en Bitcoin, con enfoque práctico en economía circular.',
  });

  const coreTopics = [
    {
      icon: Coins,
      title: 'Dinero y Economía',
      description: 'Comprende por qué Bitcoin es importante y cómo se compara con el dinero tradicional',
      details: 'Desde el trueque hasta las monedas fiat, el dinero ha evolucionado constantemente. Bitcoin representa la próxima evolución: dinero digital descentralizado que no puede ser inflado arbitrariamente por gobiernos o bancos centrales.',
      modules: [
        { name: 'Historia del Dinero', desc: 'Del trueque al patrón oro y dinero fiat' },
        { name: 'Inflación y Escasez', desc: 'Por qué el límite de 21 millones es revolucionario' },
        { name: 'Reserva de Valor', desc: 'Bitcoin como oro digital del siglo XXI' },
        { name: 'Medio de Intercambio', desc: 'Pagos peer-to-peer sin intermediarios' }
      ],
      highlights: ['21 millones de BTC máximo', 'Resistente a la inflación', 'Propiedad absoluta']
    },
    {
      icon: Shield,
      title: 'Seguridad y Privacidad',
      description: 'Aprende a asegurar tu Bitcoin y proteger tu privacidad financiera',
      details: 'La auto-custodia es fundamental en Bitcoin. Aprende a proteger tus claves privadas, usar billeteras de hardware, y mantener tu privacidad financiera en un mundo cada vez más vigilado.',
      modules: [
        { name: 'Claves Criptográficas', desc: 'Públicas, privadas y cómo funcionan' },
        { name: 'Billeteras de Hardware', desc: 'Ledger, Trezor, Coldcard y más' },
        { name: 'Multifirma (Multisig)', desc: 'Seguridad empresarial y familiar' },
        { name: 'Mejores Prácticas', desc: 'Backups, semillas y privacidad' }
      ],
      highlights: ['Not your keys, not your coins', 'Hardware wallets', 'Multifirma 2-de-3']
    },
    {
      icon: Network,
      title: 'Tecnología y Protocolo',
      description: 'Explora los fundamentos técnicos que hacen funcionar a Bitcoin',
      details: 'Bitcoin es la convergencia de criptografía, redes peer-to-peer, teoría de juegos y sistemas distribuidos. Comprende cómo estos elementos trabajan juntos para crear dinero digital incensurable.',
      modules: [
        { name: 'Prueba de Trabajo (PoW)', desc: 'Minería y seguridad de la red' },
        { name: 'Estructura de Blockchain', desc: 'Bloques, transacciones y cadena' },
        { name: 'Operación de Nodos', desc: 'Valida, no confíes (Don\'t trust, verify)' },
        { name: 'Consenso de Red', desc: 'Reglas de Nakamoto y coordinación' }
      ],
      highlights: ['10 minutos por bloque', 'SHA-256', 'Red distribuida global']
    },
    {
      icon: TrendingUp,
      title: 'Inversión y Estrategia',
      description: 'Toma decisiones informadas sobre Bitcoin como activo de inversión',
      details: 'Bitcoin ha sido el activo de mejor desempeño de la última década. Aprende estrategias probadas de acumulación, gestión de riesgo y planificación a largo plazo.',
      modules: [
        { name: 'DCA (Dollar Cost Averaging)', desc: 'Acumula Bitcoin consistentemente' },
        { name: 'Gestión de Riesgos', desc: 'Diversificación y horizonte temporal' },
        { name: 'Implicaciones Fiscales', desc: 'Impuestos en Panamá y otros países' },
        { name: 'Estrategia HODLing', desc: 'Visión a largo plazo y paciencia' }
      ],
      highlights: ['Compra y mantén', 'Piensa en décadas', 'Baja preferencia temporal']
    },
    {
      icon: Zap,
      title: 'Lightning Network',
      description: 'Domina los pagos instantáneos de bajo costo con Bitcoin',
      details: 'Lightning Network es la capa de pagos de Bitcoin, permitiendo transacciones instantáneas con comisiones mínimas. Ideal para comercios, remesas y micropagos.',
      modules: [
        { name: 'Soluciones de Capa 2', desc: 'Escalabilidad sin sacrificar seguridad' },
        { name: 'Canales de Pago', desc: 'Abre, cierra y gestiona canales' },
        { name: 'Enrutamiento y Liquidez', desc: 'Cómo fluyen los pagos en la red' },
        { name: 'Billeteras Lightning', desc: 'Phoenix, Breez, Zeus y más' }
      ],
      highlights: ['Pagos instantáneos', 'Comisiones de centavos', 'Ideal para comercios']
    },
    {
      icon: Globe,
      title: 'Bitcoin y Sociedad',
      description: 'Comprende el impacto global de Bitcoin y su futuro',
      details: 'Bitcoin está transformando el panorama financiero global, ofreciendo inclusión a los no bancarizados, reduciendo costos de remesas y democratizando el acceso al dinero sólido.',
      modules: [
        { name: 'Inclusión Financiera', desc: '1.7 mil millones sin acceso bancario' },
        { name: 'Remesas', desc: 'Envíos internacionales baratos y rápidos' },
        { name: 'Energía y Minería', desc: 'Bitcoin usa energía renovable excedente' },
        { name: 'Regulación Global', desc: 'De El Salvador a Wall Street' }
      ],
      highlights: ['Banco para los no bancarizados', 'Legal en Panamá', 'El Salvador: curso legal']
    },
    {
      icon: Store,
      title: 'Adopción en Panamá',
      description: 'Bitcoin en Ciudad de Panamá y la economía circular local',
      details: 'Panamá está emergiendo como un hub Bitcoin en Latinoamérica. Descubre cómo comercios locales están adoptando Bitcoin y construyendo una economía circular próspera.',
      modules: [
        { name: 'Comercios Locales', desc: '100+ negocios aceptando Bitcoin en Panamá' },
        { name: 'Comunidad Bitcoiner', desc: 'Meetups, educación y networking' },
        { name: 'Marco Legal', desc: 'Regulación y oportunidades en Panamá' },
        { name: 'Casos de Uso', desc: 'De cafeterías a bienes raíces' }
      ],
      highlights: ['100+ comercios', 'Comunidad activa', 'Hub regional']
    },
    {
      icon: Users,
      title: 'Minería de Bitcoin',
      description: 'Aprende cómo se crean nuevos Bitcoin y se asegura la red',
      details: 'La minería es el proceso mediante el cual se crean nuevos Bitcoin y se validan transacciones. Comprende el hardware, los pools, y las consideraciones energéticas.',
      modules: [
        { name: 'Hardware de Minería', desc: 'ASICs, consumo y rentabilidad' },
        { name: 'Mining Pools', desc: 'Colaboración y distribución de recompensas' },
        { name: 'Dificultad y Halving', desc: 'Ajustes automáticos cada 2 semanas' },
        { name: 'Minería en Panamá', desc: 'Oportunidades con energía hidroeléctrica' }
      ],
      highlights: ['Halving cada 4 años', 'Próximo: 2028', 'Energía renovable']
    },
    {
      icon: Lock,
      title: 'DeFi y Protocolos',
      description: 'Finanzas descentralizadas construidas sobre Bitcoin',
      details: 'Más allá de pagos simples, Bitcoin está expandiéndose hacia DeFi, smart contracts con RSK, y nuevos protocolos como Taproot Assets y RGB.',
      modules: [
        { name: 'Rootstock (RSK)', desc: 'Smart contracts compatibles con Ethereum' },
        { name: 'Liquid Network', desc: 'Sidechain para emisión de activos' },
        { name: 'Taproot & Schnorr', desc: 'Actualizaciones de privacidad' },
        { name: 'RGB Protocol', desc: 'Contratos inteligentes escalables' }
      ],
      highlights: ['Smart contracts', 'Stablecoins en Bitcoin', 'NFTs nativos']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
      <Header />

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-orange-100 via-amber-50 to-yellow-100 dark:from-orange-950/30 dark:via-amber-950/20 dark:to-yellow-950/30 py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTEsMTkxLDM2LDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="px-4 py-1.5 text-sm bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300 border-orange-200 dark:border-orange-800">
              <Book className="w-3.5 h-3.5 mr-1.5 inline" />
              Currículo Completo
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white">
              Temas Fundamentales de Bitcoin
            </h1>
            
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Programa exhaustivo diseñado por Bitcoin Life Panamá para llevarte desde cero hasta experto en Bitcoin,
              con enfoque práctico en economía circular y adopción local.
            </p>
          </div>
        </div>
      </div>

      {/* Core Topics Grid */}
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreTopics.map((topic, index) => {
              const Icon = topic.icon;
              return (
                <Card key={index} className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white dark:bg-gray-900 border-2 hover:border-orange-200 dark:hover:border-orange-800 overflow-hidden">
                  {/* Color accent bar */}
                  <div className="h-1.5 bg-gradient-to-r from-orange-500 to-amber-500"></div>
                  
                  <CardHeader className="space-y-3">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center mb-2 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg shadow-orange-500/40">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="text-xl group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                      {topic.title}
                    </CardTitle>
                    <CardDescription className="text-base leading-relaxed">
                      {topic.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-5">
                    {/* Details paragraph */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed border-l-2 border-orange-200 dark:border-orange-800 pl-3">
                      {topic.details}
                    </p>

                    {/* Key highlights */}
                    <div className="flex flex-wrap gap-2">
                      {topic.highlights.map((highlight, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs bg-orange-50 dark:bg-orange-950/50 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800">
                          {highlight}
                        </Badge>
                      ))}
                    </div>

                    {/* Modules list */}
                    <div className="space-y-2 pt-2">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Contenido del Módulo:
                      </div>
                      <ul className="space-y-2.5">
                        {topic.modules.map((module, idx) => (
                          <li key={idx} className="group/item">
                            <div className="flex items-start gap-2 text-sm hover:bg-orange-50 dark:hover:bg-orange-950/30 p-2 rounded-lg transition-all cursor-pointer">
                              <ChevronRight className="w-4 h-4 text-orange-500 group-hover/item:translate-x-1 transition-transform flex-shrink-0 mt-0.5" />
                              <div>
                                <div className="font-medium text-gray-900 dark:text-gray-100 group-hover/item:text-orange-600 dark:group-hover/item:text-orange-400 transition-colors">
                                  {module.name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                                  {module.desc}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA at bottom */}
                    <Link to="/lessons/beginner" className="block">
                      <Button variant="ghost" className="w-full group-hover:bg-orange-50 dark:group-hover:bg-orange-950/30 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-all mt-2">
                        <Play className="w-4 h-4 mr-2" />
                        Comenzar Módulo
                        <ChevronRight className="w-4 h-4 ml-auto group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Additional info section */}
          <div className="mt-16 text-center">
            <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border-orange-200 dark:border-orange-800">
              <CardContent className="py-8 px-6">
                <div className="max-w-3xl mx-auto space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    ¿Por qué estudiar Bitcoin con nosotros?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    Bitcoin Life Panamá es la comunidad educativa líder en Bitcoin en Panamá. Nuestro programa combina 
                    teoría sólida con aplicación práctica en el mercado local. Con más de 100 comercios usando Bitcoin 
                    en Ciudad de Panamá, tendrás la oportunidad de ver y practicar con casos de uso reales.
                  </p>
                  <div className="grid md:grid-cols-3 gap-6 pt-4">
                    <div className="space-y-2">
                      <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">9+</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Módulos Especializados</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">60+</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Lecciones Detalladas</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">100%</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Enfoque Práctico</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <DonationSection />
      <Footer />
    </div>
  );
};

export default Topics;
